import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import Select, { createFilter } from 'react-select';
import { FixedSizeList as List } from 'react-window';
import styled from 'styled-components';

import { useConfig } from '../../hooks';
import { useStores } from '../../stores';

const SelectSearch = ({ options, setValue, isDisabled, ...props }) => {
  const {
    message: { borderRadius: messageBorderRadius, botColor },
    window: { colors }
  } = useConfig();
  const { ui } = useStores();
  const { t } = useTranslation();
  const [selectValue, setSelectedValue] = React.useState(null);

  const handleChange = selectedOption => {
    setSelectedValue(selectedOption);
    setValue(selectedOption);
  };

  const customStyles = {
    control: styles => ({
      ...styles,
      minWidth: '30%',
      maxWidth: '100%',
      fontSize: '14px',
      backgroundColor: '#fff',
      borderColor: botColor,
      borderRadius: `${messageBorderRadius}px`,
      boxShadow: 'none',
      ':hover': {
        ...styles[':hover'],
        borderColor: botColor
      }
    }),
    valueContainer: styles => ({
      ...styles,
      padding: '10px 8px'
    }),
    input: styles => ({
      ...styles,
      padding: 0,
      margin: 0
    }),
    indicatorSeparator: styles => ({
      ...styles,
      backgroundColor: botColor
    }),
    dropdownIndicator: styles => ({
      ...styles,
      color: botColor,
      ':hover': {
        ...styles[':hover'],
        color: botColor
      }
    }),
    menu: styles => ({
      ...styles,
      position: 'relative'
    }),
    menuOptions: styles => ({
      ...styles,
      maxHeight: '200px'
    }),
    option: styles => ({
      ...styles,
      display: 'flex',
      alignItems: 'center',
      height: '100%',
      color: colors.main,
      ':hover': {
        ...styles[':hover'],
        color: colors.accent,
        backgroundColor: '#f5f5f5'
      },
      ':active': {
        ...styles[':active'],
        color: colors.accent,
        backgroundColor: '#f5f5f5'
      }
    })
  };

  const MenuList = innerProps => {
    const { options: currentOptions, children, getValue } = innerProps;
    const [value] = getValue();
    const initialOffset = currentOptions.indexOf(value) * 44;

    return (
      <List
        height={children.length ? 200 : 44}
        itemSize={44}
        itemCount={children.length || 1}
        initialScrollOffset={initialOffset}
      >
        {({ index, style }) => {
          if (index === 0 && !children.length) {
            return <NoOptionItem style={style}>No options</NoOptionItem>;
          }
          return <div style={style}>{children[index]}</div>;
        }}
      </List>
    );
  };

  return (
    <Root {...props}>
      <Select
        value={selectValue}
        placeholder={t('selectPlaceholder')}
        onChange={handleChange}
        options={options}
        components={{ MenuList }}
        styles={customStyles}
        hideSelectedOptions
        isDisabled={isDisabled}
        onMenuOpen={() => {
          ui.isJsonSelectOpened = true;
        }}
        filterOption={createFilter({ ignoreAccents: false })}
      />
    </Root>
  );
};

SelectSearch.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string
    })
  ).isRequired,
  setValue: PropTypes.func.isRequired,
  isDisabled: PropTypes.bool
};

const Root = styled.div`
  .fast-option {
    line-height: 1.2 !important;
  }

  div[class$='-control'] {
    width: 450px;
  }
`;

const NoOptionItem = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  color: #999;
`;

export default SelectSearch;
